import { useCallback, useState } from "react";

import { StatsOverview } from "@/components/stats/StatsOverview";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { api } from "@/config/api";
import { useAuth } from "@/contexts/auth-context";
import { useStats } from "@/hooks/use-stats";
import type { ApiResponse } from "@/types";
import { getErrorMessage } from "@/utils/error-message";

export function Profile() {
  const { user, logout } = useAuth();
  const { data: stats, isLoading, error } = useStats();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete<ApiResponse>("/api/account");
      logout();
    } catch (err) {
      setDeleteError(getErrorMessage(err));
      setDeleting(false);
    }
  }, [logout]);

  if (!user) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-navy-700">Profile</h1>

      <Card className="flex items-center gap-4">
        {user.picture ? (
          <img src={user.picture} alt="" className="h-16 w-16 rounded-full" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-navy-100 text-xl font-bold text-navy-700">
            {user.givenName?.[0] ?? user.name?.[0] ?? "?"}
          </div>
        )}
        <div>
          <p className="text-lg font-bold text-gray-900">{user.name}</p>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
      </Card>

      {isLoading && <LoadingSpinner message="Loading stats..." />}
      {error && <ErrorMessage message={getErrorMessage(error)} />}
      {stats && <StatsOverview stats={stats} />}

      <Card>
        <h2 className="mb-1 text-sm font-medium text-red-600">Danger Zone</h2>
        <p className="mb-4 text-xs text-gray-500">
          Permanently delete your account and all associated data. This cannot be undone.
        </p>

        {deleteError && <ErrorMessage message={deleteError} />}

        {!showConfirm ? (
          <Button variant="danger" onClick={() => setShowConfirm(true)} className="w-full">
            Delete Account
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium text-red-700">
              Are you sure? All your progress, stats, achievements, and history will be permanently erased.
            </p>
            <div className="flex gap-3">
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1"
              >
                {deleting ? "Deleting..." : "Yes, Delete Everything"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowConfirm(false)}
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
